import React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

/**
 * 🔴 FL-88 — one WORKED arc of the ring, window-relative and already clamped to [0,1] BY THE SERVER.
 *
 * A session with a break arrives as TWO segments, and that is load-bearing rather than decorative:
 * the number under the ring subtracts breaks, so a break that did not open a gap in the arc would
 * draw more filled time than the number claims.
 *
 * The client never derives these from timestamps. Geometry and membership are server truth.
 */
export interface TimerSegment {
  start: number;
  end: number;
  /** Still running. The client may tick this one. */
  isOpen?: boolean;
}

interface ITimerProps {
  thickness?: number;
  precision?: number;
  /** Legacy single-arc inputs, still used by the dashboard widget. Ignored when `segments` is given. */
  progress?: number;
  startProgress?: number;
  /** 🔴 FL-88 — multi-segment ring. Takes precedence over `progress`/`startProgress`. */
  segments?: TimerSegment[];
  fontSize?: number;
  text?: string;
  secondaryText?: string;
  primaryColor?: string;
  secondaryColor?: string;
  style?: React.CSSProperties;
  radius?: number;
  stroke?: "butt" | "round" | "square" | "inherit";
  trackTooltip?: React.ReactNode;
  progressTooltip?: React.ReactNode;
  primaryTextTooltip?: React.ReactNode;
  secondaryTextTooltip?: React.ReactNode;
}

const SvgTooltip = ({ tooltip, children }: { tooltip?: React.ReactNode; children: React.ReactElement }) => {
  if (!tooltip) return children;
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          className="z-50 max-w-[16rem] rounded-md bg-foreground px-2.5 py-2 text-xs text-background shadow-lg"
          sideOffset={6}
          collisionPadding={8}
        >
          {tooltip}
          <TooltipPrimitive.Arrow className="fill-foreground" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
};

/**
 * 🔴 FL-88 — the ring is drawn TRUTHFULLY. There is deliberately no minimum sweep and no minimum
 * gap here, and that is a decision rather than an omission.
 *
 * The previous version inflated a short segment to 2° and forced a 4° gap between neighbours so
 * that a break would always be *visible*. It bought nothing: at the clocking page's geometry a
 * round cap adds half the stroke (40 units of a 1000-unit viewBox) to each endpoint, so a 4° gap
 * — 29 units of arc at radius 420 — was swallowed whole and the two segments still rendered as one
 * solid capsule. What the inflation did achieve was a falsehood: a real 1.14-second gap was drawn
 * at 4°, which reads as roughly two minutes of not-working that never happened.
 *
 * So: every angle below is the segment the server actually reported. A gap smaller than about a
 * pixel of arc is physically unrenderable and simply will not appear — the hover punch list is the
 * audit path, and it shows every punch exactly.
 */

const Timer: React.FC<ITimerProps> = ({
  thickness = 8,
  precision = 1000,
  progress = 0,
  startProgress = 0,
  segments,
  fontSize = 16,
  text = "",
  secondaryText = "",
  primaryColor = "#304EA1",
  secondaryColor = "#D8D9DB",
  radius = 240,
  stroke = "round",
  style,
  trackTooltip,
  progressTooltip,
  primaryTextTooltip,
  secondaryTextTooltip,
}) => {
  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;

    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians)),
    };
  };

  const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
    // Guard against invalid inputs producing NaN in path
    if (!Number.isFinite(radius) || !Number.isFinite(startAngle) || !Number.isFinite(endAngle)) {
      return "";
    }

    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);

    if (!Number.isFinite(start.x) || !Number.isFinite(start.y) || !Number.isFinite(end.x) || !Number.isFinite(end.y)) {
      return "";
    }

    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    const d = [
      "M", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
    ].join(" ");

    return d;
  };

  const calculateParams = () => {
    const viewboxHeight = precision;
    const viewboxWidth = precision;
    const calculatedThickness = thickness * precision / 100;
    const calculatedFontSize = fontSize * precision / 100;

    const arcRadius = radius;

    const centerX = viewboxWidth / 2;
    const centerY = viewboxHeight / 2;

    const emptyRadius = 360 - arcRadius;
    const halfEmptyRadius = emptyRadius / 2;
    const startAngle = halfEmptyRadius - 180;
    const endAngle = 180 - halfEmptyRadius;
    const clamp01 = (v: any) => (Number.isFinite(v) ? Math.min(Math.max(Number(v), 0), 1) : 0);

    const angleAt = (p: number) => (arcRadius * p) - 180 + halfEmptyRadius;
    const calculatedRadius = Math.max(0, Math.min(viewboxWidth, viewboxHeight) / 2 - calculatedThickness);

    // ── Multi-segment ring (FL-88). Nothing here interprets a timestamp: the server already scaled
    // every segment to the window and clamped it, so this is pure angle arithmetic.
    const arcs: { d: string; isOpen: boolean }[] = [];
    if (segments && segments.length > 0) {
      // Server truth is already ordered. Sorting is a cheap defence that is a no-op on well-formed
      // input and keeps the overlap guard below meaningful if it ever stops being well-formed.
      const ordered = [...segments].sort((a, b) => clamp01(a?.start) - clamp01(b?.start));
      let previousEndAngle: number | null = null;

      for (const segment of ordered) {
        let a0 = angleAt(clamp01(segment?.start));
        let a1 = angleAt(clamp01(segment?.end));

        // Trim an overlap instead of painting the same minute twice. This can only ever SHORTEN a
        // segment, so unlike the old minimum-gap rule it cannot invent time the punches don't show.
        if (previousEndAngle !== null && a0 < previousEndAngle) a0 = previousEndAngle;
        if (a1 > endAngle) a1 = endAngle;

        // Zero-length, inverted, fully overlapped, or entirely past the window end: draw nothing.
        // Nothing is the honest rendering, and with a butt cap it is also literally nothing rather
        // than a stray dot.
        if (!(a1 > a0)) continue;

        arcs.push({ d: describeArc(centerX, centerY, calculatedRadius, a0, a1), isOpen: !!segment?.isOpen });
        previousEndAngle = a1;
      }
    } else {
      // Legacy single arc, kept for the dashboard widget.
      const safeEndProgress = clamp01(progress);
      const safeStartProgress = clamp01(startProgress);
      const segmentStartProgress = Math.min(safeStartProgress, safeEndProgress);
      const segmentEndProgress = Math.max(safeStartProgress, safeEndProgress);
      if (segmentEndProgress > segmentStartProgress) {
        arcs.push({
          d: describeArc(centerX, centerY, calculatedRadius, angleAt(segmentStartProgress), angleAt(segmentEndProgress)),
          isOpen: false,
        });
      }
    }

    // 🔴 FL-88 — a round cap extends the painted arc by half the stroke BEYOND each endpoint. On a
    // segmented ring that is twice wrong: the two caps facing each other across a break close the
    // gap, and each one paints time the number under the ring has already subtracted. Butt caps
    // make the painted extent equal the reported extent.
    //
    // 🔴 OWNER DECISION 2026-09-11, revising an earlier one: butt caps on EVERY segment, including a
    // lone one. The first version kept the rounded terminal for a single arc on the reasoning that it
    // has nothing to separate itself from — measurement killed that argument. A round cap paints a
    // disc of the full stroke DIAMETER, which here is 10.91 deg = 4.5% of the ring, so a sub-pixel
    // single session draws as ~21.8 MINUTES of work on an 8h window. That is the same falsehood the
    // gaps fix removed, surviving in the COMMON case (one session, an ordinary day) rather than the
    // rare one. Truthfulness was the ruling; it applies here too.
    //
    // Scope: the SEGMENTS path only. The legacy `progress` branch keeps `stroke` untouched, which is
    // what leaves the dashboard widget pixel-identical (measured: same `d`, same linecap, before and
    // after). That widget can still paint a phantom dot at a near-zero `progress` — filed, not fixed
    // here, and it renders garbage text anyway (it passes a `moment.duration` into a formatter that
    // takes seconds).
    // `as const` so it narrows to the literal rather than widening to `string`, which the
    // `strokeLinecap` prop rejects.
    const segmentCap = "butt" as const;

    return {
      arcPath: describeArc(centerX, centerY, calculatedRadius, startAngle, endAngle),
      arcs,
      segmentCap,
      viewboxWidth,
      viewboxHeight,
      calculatedThickness,
      calculatedFontSize,
    };
  };

  const {
    arcPath,
    arcs,
    segmentCap,
    viewboxWidth,
    viewboxHeight,
    calculatedThickness,
    calculatedFontSize,
  } = calculateParams();

  return (
    <TooltipPrimitive.Provider delayDuration={200}>
      <svg
        viewBox={`0 0 ${viewboxWidth} ${viewboxHeight}`}
        style={style}
        role="img"
        aria-label={text ? `Timer: ${text}` : "Timer"}
      >
        <SvgTooltip tooltip={trackTooltip}>
          <g>
            <path
              strokeLinecap={stroke}
              stroke={secondaryColor}
              fill="none"
              strokeWidth={calculatedThickness}
              d={arcPath}
              shapeRendering="geometricPrecision"
            />
          </g>
        </SvgTooltip>
        <SvgTooltip tooltip={progressTooltip}>
          <g>
            {arcs.map((arc, index) => (
              <path
                key={index}
                strokeLinecap={segmentCap}
                stroke={primaryColor}
                fill="none"
                strokeWidth={calculatedThickness}
                d={arc.d}
                shapeRendering="geometricPrecision"
              />
            ))}
          </g>
        </SvgTooltip>
        <SvgTooltip tooltip={primaryTextTooltip}>
          <text x="50%" y="50%" dominantBaseline="middle" fontSize={calculatedFontSize}
            textAnchor="middle">
            {text}
          </text>
        </SvgTooltip>
        <SvgTooltip tooltip={secondaryTextTooltip}>
          <text x="50%" y="65%" dominantBaseline="middle" fontSize={calculatedFontSize * 0.4}
            textAnchor="middle">
            {secondaryText}
          </text>
        </SvgTooltip>
      </svg>
    </TooltipPrimitive.Provider>
  );
};

export default Timer;
